import {
  GridRenderCellParams,
  GridValueFormatterParams,
  GridValueGetterParams,
} from '@mui/x-data-grid-premium';
import _ from 'lodash';
import { ALL_APPS } from 'constants/appConstants';
import { statusTextMapper } from 'constants/datagrid';
import {
  statusColorCallback,
  textPill,
} from 'ui-component/DataGrid/GridColTypeDef';
import {
  BooleanIconRenderCell,
  renderPartCell,
  renderQuantity,
  RenderWithCopyButton,
} from 'ui-component/DataGrid/Render';
import {
  currencyValueFormatter,
  dateValueFormatter,
} from 'ui-component/DataGrid/ValueFormatters';
import {
  MslValueOptions,
  packageValueOptions,
  tapeSizeOptions,
} from 'types/inventory';
import { mslFloorLifeColumnText, mslRemainingFloorLife } from 'utils/functions';
import { ExtendedGridColDef } from 'types/datagrid';
import { appPermissionAccessLevels } from 'types/apps';
import { IconAlertOctagon } from '@tabler/icons-react';
import { convertDate } from 'utils/dates';
import { Box } from '@mui/material';
import { getApplyFilterFnPartInStockLot } from 'ui-component/DataGrid/FilterOperators';
import { FieldValues } from 'react-hook-form';
import { useUpdateShipmentLineMutation } from 'store/slices/apiV1/inventory';

const columns: ExtendedGridColDef[] = [
  {
    field: 'part',
    headerName: 'Part',
    description: 'Manufacturer and part number',
    type: 'string',
    hideable: false,
    minWidth: 250,
    flex: 1,
    renderCell: (params: GridRenderCellParams<string>) =>
      renderPartCell({
        part: params.row.stockLot?.part ?? null,
        noPart: null,
        altCount: null,
        stockLot: params.row.stockLot ?? null,
        params,
      }),
    valueGetter: (params: GridValueGetterParams) => {
      if (
        !params.row.stockLot?.part ||
        typeof params.row.stockLot.part === 'string'
      )
        return '-';
      return params.row.stockLot.part.mpn;
    },
    getApplyQuickFilterFn: getApplyFilterFnPartInStockLot,
    permissionScope: { app: ALL_APPS.PART_SEARCH.id },
  },
  {
    field: 'status',
    headerName: 'Status',
    description: 'Status of the shipped stock lot',
    type: 'singleSelect',
    valueOptions: ['Pending', 'Picked', 'Packed', 'Ingested'],
    minWidth: 100,
    valueGetter: (params: GridValueGetterParams) =>
      statusTextMapper[params.row.status],
    ...textPill(statusColorCallback, true),
    flex: 0.6,
    permissionScope: { app: ALL_APPS.STOCK.id },
  },
  {
    field: 'ownedQuant',
    headerName: 'Lot Quantity',
    description: 'Current unit quantity in this stock lot',
    type: 'number',
    minWidth: 100,
    valueGetter: (params: GridValueGetterParams) =>
      params.row.stockLot?.quant || 0,
    renderCell: (params: GridRenderCellParams<string>) =>
      renderQuantity(
        params.row.stockLot?.quant || 0,
        params.row.stockLot?.quantCertain,
        params.row.allocation?.inventory?.totalFree <
          (params.row.stockLot?.quant || 0)
      ),
    flex: 0.5,
    permissionScope: { app: ALL_APPS.STOCK.id },
  },
  {
    field: 'shippedQuant',
    headerName: 'Shipped Quantity',
    description: 'Shipped quantity in this stock lot',
    type: 'number',
    minWidth: 100,
    valueGetter: (params: GridValueGetterParams) =>
      params.row.shippedQuant || 0,
    flex: 0.5,
    permissionScope: { app: ALL_APPS.KITTING.id },
  },
  {
    field: 'expectedDate',
    headerName: 'Expected Delivery',
    description: 'Expected delivery date of expected stock lots',
    type: 'date',
    hideable: true,
    minWidth: 100,
    valueGetter: (params: GridValueGetterParams) => {
      if (!params.row.stockLot?.expectedDate) return null;
      return convertDate(params.row.stockLot.expectedDate);
    },
    valueFormatter: dateValueFormatter,
    flex: 1,
    permissionScope: { app: ALL_APPS.STOCK.id },
  },
  {
    field: 'bonded',
    headerName: 'Re-Export By',
    description:
      'Date by which a stock lot imported under a temporary import bond must be re-exported',
    type: 'date',
    hideable: true,
    minWidth: 100,
    valueGetter: (params: GridValueGetterParams) => {
      if (!params.row.stockLot?.bonded) return null;
      return convertDate(params.row.stockLot.bonded);
    },
    valueFormatter: dateValueFormatter,
    flex: 1,
    permissionScope: { app: ALL_APPS.STOCK.id },
  },
  {
    field: 'package',
    headerName: 'Package',
    description: 'Indicates how this stock lot is packaged currently',
    type: 'singleSelect',
    minWidth: 100,
    flex: 0.5,
    valueOptions: packageValueOptions,
    valueGetter: (params: GridValueGetterParams) =>
      params.row.stockLot?.package,
    valueFormatter: ({ value }: GridValueFormatterParams) => {
      const option = _.find(packageValueOptions, ['value', value]);
      if (option) {
        return option.label;
      }
      return '-';
    },
    permissionScope: { app: ALL_APPS.STOCK.id },
  },
  {
    field: 'dateCode',
    headerName: 'Date Code',
    description: 'Manufacturer’s date code for this stock lot',
    type: 'string',
    minWidth: 100,
    valueGetter: (params: GridValueGetterParams) =>
      params.row.stockLot?.dateCode || '-',
    flex: 0.5,
    permissionScope: { app: ALL_APPS.STOCK.id },
  },
  {
    field: 'mfgLot',
    headerName: 'Manufacturer Lot ID',
    description: 'Manufacturer’s lot ID or serial for this stock lot',
    type: 'string',
    minWidth: 100,
    valueGetter: (params: GridValueGetterParams) =>
      params.row.stockLot?.mfgLot || '-',
    flex: 0.5,
    permissionScope: { app: ALL_APPS.STOCK.id },
  },
  {
    field: 'cofactrId',
    headerName: 'Cofactr Lot ID',
    description: 'Cofactr’s unique ID for this stock lot',
    type: 'string',
    minWidth: 180,
    valueGetter: (params: GridValueGetterParams) =>
      (typeof params.row?.stockLot?.part === 'string'
        ? params.row?.stockLot?.part
        : params.row.stockLot?.part?.id
      )?.concat(params.row?.stockLot?.lotId || '') || '-',
    flex: 0.5,
    renderCell: (params: GridRenderCellParams<string>) =>
      RenderWithCopyButton(
        <>
          {(params.value as string).slice(0, 12)}
          <Box component="span" fontWeight="bold">
            {(params.value as string).slice(-5)}
          </Box>
        </>,
        (params.value as string).slice(-5),
        'lot ID'
      ),
    permissionScope: { app: ALL_APPS.STOCK.id },
  },
  {
    field: 'quarantined',
    headerName: 'Quarantined',
    description:
      'Indicates whether this stock lot has been quarantined due to a suspected issue',
    type: 'boolean',
    minWidth: 60,
    valueGetter: (params: GridValueGetterParams) =>
      params.row.stockLot?.quarantined,
    valueFormatter: (params: GridValueFormatterParams) => params.value || '-',
    renderCell: (params: GridRenderCellParams) =>
      BooleanIconRenderCell(params, IconAlertOctagon, null, 'error', 'primary'),
    flex: 0.2,
    permissionScope: { app: ALL_APPS.STOCK.id },
  },
  {
    field: 'msl',
    headerName: 'MSL',
    description: 'Moisture sensitivity level of this part',
    type: 'singleSelect',
    valueOptions: MslValueOptions,
    minWidth: 60,
    valueGetter: (params: GridValueGetterParams) =>
      params.row.stockLot?.msl || '-',
    flex: 0.2,
    permissionScope: { app: ALL_APPS.STOCK.id },
  },
  {
    field: 'mslFloorHours',
    headerName: 'MSL Floor Life',
    description: 'Number of remaining hours of floor life before next bake',
    type: 'number',
    minWidth: 100,
    flex: 0.5,
    valueGetter: (params: GridValueGetterParams) =>
      mslRemainingFloorLife(
        params.row.stockLot?.msl,
        params.row.stockLot?.mslFloorHours
      ),
    valueFormatter: (params: GridValueFormatterParams) =>
      mslFloorLifeColumnText(params.value),
    permissionScope: { app: ALL_APPS.STOCK.id },
  },
  {
    field: 'countryOfOrigin',
    headerName: 'Country of Origin',
    description: 'Country of manufacturing origin',
    type: 'string',
    minWidth: 60,
    valueGetter: (params: GridValueGetterParams) =>
      params.row.stockLot?.countryOfOrigin || '-',
    flex: 0.2,
    permissionScope: { app: ALL_APPS.STOCK.id },
  },
  {
    field: 'tapeSize',
    headerName: 'Tape Size',
    description: 'Size of cut tape or reel that this stock lot is packaged in',
    type: 'singleSelect',
    valueOptions: tapeSizeOptions,
    minWidth: 60,
    flex: 0.2,
    valueGetter: (params: GridValueGetterParams) =>
      params.row.stockLot?.tapeSize ? `${params.row.stockLot.tapeSize}mm` : '-',
    permissionScope: { app: ALL_APPS.STOCK.id },
  },
  {
    field: 'stockLot.externalNotes',
    headerName: 'Stock Lot Notes',
    description: 'Notes associated with stock lot',
    type: 'string',
    flex: 0.5,
    valueGetter: (params: GridValueGetterParams) =>
      params.row.stockLot?.externalNotes || '-',
    permissionScope: { app: ALL_APPS.STOCK.id },
  },
  {
    field: 'externalNotes',
    headerName: 'Shipping Line Notes',
    description: 'Notes associated with this shipment',
    type: 'string',
    flex: 0.5,
    valueGetter: (params: GridValueGetterParams) =>
      params.row.externalNotes || '-',
    permissionScope: { app: ALL_APPS.KITTING.id },
    editPermissionScope: {
      app: ALL_APPS.KITTING.id,
      accessLevel: appPermissionAccessLevels.edit,
    },
  },
  {
    field: 'printedNotes',
    headerName: 'Stock Lot Label Notes',
    description:
      'Notes to be printed on stock lot shipment label and packing slip',
    type: 'string',
    flex: 0.5,
    editable: true,
    inlineEditRenderCell: true,
    useMutation: useUpdateShipmentLineMutation,
    getMutationArgs: (data: FieldValues, params: GridRenderCellParams) => ({
      shipmentId: params.row.shipment,
      shipmentLineId: params.row.id,
      payload: data,
    }),
    valueGetter: (params: GridValueGetterParams) =>
      params.row.printedNotes || '-',
    permissionScope: { app: ALL_APPS.KITTING.id },
    editPermissionScope: {
      app: ALL_APPS.KITTING.id,
      accessLevel: appPermissionAccessLevels.edit,
    },
  },
  {
    field: 'unitPrice',
    headerName: 'Unit Price',
    description: 'Unit price paid for this stock lot',
    type: 'number',
    minWidth: 80,
    flex: 0.5,
    valueGetter: (params: GridValueGetterParams) =>
      params.row.stockLot?.unitPrice,
    valueFormatter: currencyValueFormatter,
  },
  {
    field: 'landedUnitPrice',
    headerName: 'Landed Unit Price',
    description:
      'Unit price paid for this stock lot inclusive of shipping, etc',
    type: 'number',
    minWidth: 80,
    flex: 0.5,
    valueGetter: (params: GridValueGetterParams) =>
      params.row.stockLot?.landedUnitPrice,
    valueFormatter: currencyValueFormatter,
  },
  {
    field: 'totalPrice',
    headerName: 'Total Price',
    description: 'Price paid for the portion of this stock lot being shipped',
    type: 'number',
    minWidth: 80,
    flex: 0.5,
    valueGetter: (params: GridValueGetterParams) =>
      (params.row.stockLot?.unitPrice || 0) *
      (params.row.quantAfterApproval || params.row.stockLot?.quant || 0),
    valueFormatter: currencyValueFormatter,
  },
  {
    field: 'landedTotalPrice',
    headerName: 'Landed Total Price',
    description:
      'Price paid for the portion of this stock lot being shipped inclusive of shipping, etc',
    type: 'number',
    minWidth: 80,
    flex: 0.5,
    valueGetter: (params: GridValueGetterParams) =>
      (params.row.stockLot?.landedUnitPrice || 0) *
      (params.row.quantAfterApproval || params.row.stockLot?.quant || 0),
    valueFormatter: currencyValueFormatter,
  },
];

export default columns;
